import React from "react";

const getHead = (node: HTMLElement | null) =>
  node?.getElementsByTagName("thead")[0]?.getElementsByTagName("tr")[0];

type GetListControlsParams<T> = Pick<
  ListViewProps<T, keyof T, any>,
  "pinFirstColumn" | "columns" | "data" | "identityKey"
> & {
  tableRef: React.RefObject<HTMLTableElement | null>;
};

export const getColumnElementFn =
  <T>({ columns, tableRef }: GetListControlsParams<T>) =>
  (key: keyof T) => {
    const index = columns
      .filter((c) => c.show ?? true)
      .findIndex((column) => key === column.key);
    return getHead(tableRef.current)?.children.item(index);
  };

const getScrollValueToColumnFn =
  <T>(params: GetListControlsParams<T>) =>
  (key: keyof T) => {
    const { pinFirstColumn, tableRef } = params;
    const getColumnElement = getColumnElementFn(params);

    const offset = pinFirstColumn
      ? getHead(tableRef.current)?.children.item(0)?.getBoundingClientRect()
          .right ?? 0
      : 0;
    const scrollX =
      getColumnElement(key)?.getBoundingClientRect().x ?? undefined;
    if (scrollX === undefined) return;
    return scrollX - offset;
  };

export const getListControls = <T>(
  params: GetListControlsParams<T>
): ListControls<T> => {
  const { tableRef } = params;
  const getColumnElement = getColumnElementFn(params);
  const getScrollValueToColumn = getScrollValueToColumnFn(params);

  const scrollTo = (key: keyof T, behavior: ScrollBehavior = "smooth") => {
    const scrollingElement = tableRef.current?.parentElement ?? null;
    const scrollValue = getScrollValueToColumn(key);

    if (scrollingElement === null) return;
    scrollingElement.scrollBy({ left: scrollValue, behavior });
  };

  const scrollToNext = (keys: (keyof T)[]) => {
    // In pixel.
    const DELTA_MIN_SCROLL_DISTANCE = 8;
    const scrollingElement = tableRef.current?.parentElement ?? null;
    const scrollValues = keys
      .map((key) => ({
        element: getColumnElement(key) ?? undefined,
        scrollBy: getScrollValueToColumn(key),
      }))
      .filter((x) => x.element !== undefined && x.scrollBy !== undefined) as {
      element: Element;
      scrollBy: number;
    }[];

    scrollValues.sort((a, b) => a.scrollBy - b.scrollBy);

    if (scrollingElement === null || scrollValues.length === 0) return;

    let nextValue = scrollValues.find(
      (x) => x.scrollBy > DELTA_MIN_SCROLL_DISTANCE
    );

    if (
      nextValue === undefined ||
      // If we've reached the end of the scrollable area, go back to the first value
      scrollingElement.scrollWidth - scrollingElement.scrollLeft ===
        scrollingElement.clientWidth
    )
      nextValue = scrollValues[0];
    scrollingElement.scrollBy({
      left: nextValue?.scrollBy,
      behavior: "smooth",
    });
  };

  const scrollToPosition = (options: ScrollToOptions) => {
    tableRef.current?.parentElement?.scrollTo(options);
  };

  return { scrollTo, scrollToNext, scrollToPosition };
};
